import { writable, derived } from 'svelte/store';
import API from './api';
import OPTS from './options';
import reduce from 'lodash/reduce';

const SOLR_SPECIAL_CHARS = /([+\-&|!(){}[\]^~*?:\\/])/g;

export class FreeTerm {
    constructor(value) {
        this.value = value;
    }

    eq(that) {
        return that instanceof FreeTerm &&
            (this.value === that.value) ||
            (typeof that === 'string' && that === this.value);
    }

    toString() {
        return this.value;
    }
}
export class Filter {
    constructor(label, fq) {
        this.label = label;
        this.fq = fq;
    }

    eq(that) {
        return (that instanceof Filter &&
            this.label === that.label &&
            this.fq === that.fq) ||
            (typeof that === 'string' && that === this.fq);
    }

    toString() {
        return this.label;
    }
}

const storage = window.sessionStorage;

function newQueryStore() {
    let storedValue = null;
    if (OPTS.session_storage && storage.getItem(OPTS.ssid)) {
        const ssvalue = JSON.parse(storage.getItem(OPTS.ssid));

        ssvalue.free_terms = ssvalue.free_terms.map(it => new FreeTerm(it.value));
        ssvalue.filters = ssvalue.filters.map(it => new Filter(it.label, it.fq));

        storedValue = ssvalue;
    }

    const { subscribe, update } = writable(storedValue ? storedValue : {
        free_terms: OPTS.first_query ? [new FreeTerm(OPTS.first_query)] : [], // string
        filters: [], // Filter
        active: OPTS.mode_multi ? OPTS.archives[0].name : 'all',
        current_page: 1
    });

    subscribe(value => {
        storage.setItem(OPTS.ssid, JSON.stringify(value));
    });

    return {
        subscribe,
        add(criteria) {
            this.modify(null, criteria);
        },
        remove(criteria) {
            this.modify(criteria, null);
        },
        modify(removes, adds) {
            update($q => {
                let status = {...$q};

                // remove elements if any
                if (removes){
                    removes = Array.isArray(removes) ? removes : [removes];
                    removes.forEach(criteria => {
                        if (criteria instanceof Filter) {
                            status = { ...status, filters: status.filters.filter(it => !it.eq(criteria)) };
                        } else if( criteria instanceof FreeTerm) {
                            status = { ...status, free_terms: status.free_terms.filter(it => !it.eq(criteria)) };
                        }
                    });
                }

                // adds elements if any
                if (adds) {
                    adds = Array.isArray(adds) ? adds : [adds];
                    adds.forEach(criteria => {
                        if (criteria instanceof Filter) {
                            status = { ...status, filters: [...status.filters, criteria] };
                        } else if( criteria instanceof FreeTerm) {
                            // if the term already exists do nothing
                            if (status.free_terms.find(it => it.eq(criteria)))
                                return;

                            status =  { ...status, free_terms: [...status.free_terms, criteria] }
                        }
                    });
                }

                if (adds || removes) {
                   status = {...status, current_page: 1};
                }

                return status;
            });
        },
        setActive(id) {
            update($q => ({...$q, active: id, current_page: 1}))
        },
        setPage(page) {
            update($q => ({...$q, current_page: page}))
        },
        clear() {
            update($q => ({ ...$q, free_terms: [], filters: [], current_page: 1}))
        }
    }
}

export const query = newQueryStore();

export const search_terms = derived(query, $query => [...$query.free_terms, ...$query.filters]);


function multiResult($query, set) {
    // only update the store with the latest query
    let is_latest = true;

    // common parameters for all archives
    const common_params = {
        query: $query.free_terms
            .map(term => `_text_:(${term.value.replace(SOLR_SPECIAL_CHARS, "\\$1")})`)
            .join(" AND ") || "*:*",
        filters:[...$query.filters.map(it => it.fq)],
    }

    // make one query for each configured archive, adding specific configuration
    const requests = OPTS.archives.map(archive => {
        let base_filters = archive.base_filters.map(it => typeof it === 'string' ? it : `${it.field}:(${it.value})`);

        return API.search({
            ...common_params,
            filters:[...base_filters, ...common_params.filters],
            paging: $query.active === archive.name
                ? {start: ($query.current_page - 1) * OPTS.perpage, limit: OPTS.perpage}
                : null,
            facets: $query.active === archive.name ? [archive.funds_field, ...archive.filters.map(it => it.field)] : null
        });
    });

    // wait on all requests to finish to produce the final value
    Promise.all(requests).then(results => reduce(results, (acc, entry, idx) => {
        acc[OPTS.archives[idx].name] = entry;
        return acc;
    }, {})).then(data => is_latest && set(data));

    return () => is_latest = false // flag the request as old
}

function singleResult($query, set) {
    // only update the store with the latest query
    let is_latest = true;

    const archive = OPTS.archives[0];
    // common parameters for all archives
    const common_params = {
        query: $query.free_terms
            .map(term => `_text_:(${term.value.replace(SOLR_SPECIAL_CHARS, "\\$1")})`)
            .join(" AND ") || "*:*",
        filters:[...$query.filters.map(it => it.fq)],
    }

    if(archive.base_query) {
        if (common_params.query === '*:*') {
            common_params.query = archive.base_query;
        } else {
            common_params.query = `(${archive.base_query}) AND ${common_params.query}` ;
        }
    }

    // make one query for each configured archive, adding specific configuration
    const funds = [{value: 'all'}, ...archive.funds];
    const requests = funds.map(fund => {
        let base_filters = fund.value === 'all' ? [] : [`${archive.funds_field}:(${fund.value})`];
        base_filters = [...base_filters, ...archive.base_filters.map(it => typeof it === 'string' ? it : `${it.field}:(${it.value})`)];

        return API.search({
            ...common_params,
            filters:[...base_filters, ...common_params.filters],
            paging: $query.active === fund.value
                ? {start: ($query.current_page - 1) * OPTS.perpage, limit: OPTS.perpage}
                : null,
            facets: $query.active === fund.value ? [...archive.filters.map(it => it.field)] : null
        });
    });

    // wait on all requests to finish to produce the final value
    Promise.all(requests).then(results => reduce(results, (acc, entry, idx) => {
        acc[funds[idx].value] = entry;
        return acc;
    }, {})).then(data => is_latest && set(data));

    return () => is_latest = false // flag the request as old
}

export const result = derived(query, OPTS.mode_multi ? multiResult : singleResult, { });

export const total_count = derived(result, $result => reduce($result, (acc, value) => acc + value.total , OPTS.mode_single && $result['all'] ? -($result['all'].total) : 0), 0)

export const current_result = derived([query, result], ([$q, $r]) => ($r[$q.active] || { total: 0, docs: [], facets: {} }))
/*
*
* result = {
*   "fotografico" : { result: 20 },
*   "documentale": { result: 50 }
* }
*
* */
